import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Building2, Save, Bell, Camera, Globe, MapPin, Phone,
  Award, Linkedin, X, Upload, Loader2, Plus, Pencil,
  Trash2, Star, CheckCircle2, AlertCircle, Clock,
} from "lucide-react";
import { useGaContext } from "@/hooks/useGaContext";

// ── Constants ─────────────────────────────────────────────────────────────────

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

const LICENSE_TYPES = [
  "Life & Health",
  "Property & Casualty",
  "Life Only",
  "Health Only",
  "Accident & Health",
  "Variable Products",
  "General Lines",
  "Managing General Agent (MGA)",
  "Third Party Administrator (TPA)",
  "Surplus Lines",
  "Other",
];

const LICENSE_STATUSES = [
  { value: "active", label: "Active", icon: CheckCircle2, color: "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800" },
  { value: "pending", label: "Pending", icon: Clock, color: "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800" },
  { value: "expired", label: "Expired", icon: AlertCircle, color: "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800" },
  { value: "suspended", label: "Suspended", icon: AlertCircle, color: "text-muted-foreground bg-muted border-border" },
];

const ALERT_TYPES = [
  { key: "broker_never_logged_in", label: "Broker Never Logged In", description: "Alert when a linked broker has never logged into Administr" },
  { key: "broker_first_login", label: "Broker First Login", description: "Notify when a broker logs in for the very first time" },
  { key: "low_adoption", label: "Low Adoption Score", description: "Alert when a broker's adoption score drops below the Building threshold" },
  { key: "license_expiry", label: "License Expiring Soon", description: "Alert when a broker's license is expiring within 30 days" },
  { key: "broker_graduated", label: "Broker Graduated to Power User", description: "Notify when a broker reaches Power User adoption band" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function LicenseStatusBadge({ status }: { status: string }) {
  const cfg = LICENSE_STATUSES.find((s) => s.value === status) ?? LICENSE_STATUSES[0];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function isExpiringSoon(dateStr?: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const diff = d.getTime() - Date.now();
  return diff > 0 && diff < 60 * 24 * 60 * 60 * 1000; // 60 days
}

// ── Logo Upload ───────────────────────────────────────────────────────────────

function LogoUpload({ gaId, logoObjectPath, onUploaded }: {
  gaId: string;
  logoObjectPath?: string | null;
  onUploaded: () => void;
}) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!logoObjectPath) { setPreviewSrc(null); return; }
    setLoadError(false);
    setPreviewSrc(`/api/ga/${gaId}/logo?t=${Date.now()}`);
  }, [logoObjectPath, gaId]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file.", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max size is 10 MB.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const urlRes = await fetch(`/api/ga/${gaId}/logo/upload-url`, { method: "POST" });
      if (!urlRes.ok) throw new Error("Failed to get upload URL");
      const { uploadURL, objectPath } = await urlRes.json();
      const putRes = await fetch(uploadURL, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      if (!putRes.ok) throw new Error("Upload failed");
      const saveRes = await fetch(`/api/ga/${gaId}/logo`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ objectPath }),
      });
      if (!saveRes.ok) throw new Error("Failed to save logo reference");
      setPreviewSrc(URL.createObjectURL(file));
      setLoadError(false);
      toast({ title: "Logo uploaded" });
      onUploaded();
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await fetch(`/api/ga/${gaId}/logo`, { method: "DELETE" });
      setPreviewSrc(null);
      toast({ title: "Logo removed" });
      onUploaded();
    } catch {
      toast({ title: "Error", description: "Failed to remove logo.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const hasLogo = !!previewSrc && !loadError;

  return (
    <div className="flex items-start gap-5">
      <div className="relative group shrink-0">
        <div
          className="w-24 h-24 rounded-xl border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors"
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {hasLogo ? (
            <img src={previewSrc!} alt="Agency logo" className="w-full h-full object-contain p-1" onError={() => setLoadError(true)} />
          ) : (
            <div className="flex flex-col items-center gap-1 text-muted-foreground">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin text-blue-500 dark:text-blue-400" /> : (
                <><Building2 className="w-7 h-7" /><span className="text-[10px] font-medium">Add logo</span></>
              )}
            </div>
          )}
          {hasLogo && !uploading && (
            <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-5 h-5 text-white" />
            </div>
          )}
        </div>
        {hasLogo && !uploading && !deleting && (
          <button onClick={(e) => { e.stopPropagation(); handleDelete(); }}
            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 shadow transition-colors">
            <X className="w-3 h-3" />
          </button>
        )}
        {deleting && (
          <div className="absolute inset-0 bg-background/80 rounded-xl flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
      <div className="space-y-1.5 pt-1">
        <p className="text-sm font-medium text-foreground">Agency Logo</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Upload your agency's logo — displayed on the GA portal, broker-facing pages, and documents.<br />
          PNG, JPG, SVG or WebP · Max 10 MB
        </p>
        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="mt-1">
          {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading…</> : <><Upload className="w-3.5 h-3.5 mr-1.5" />{hasLogo ? "Replace Logo" : "Upload Logo"}</>}
        </Button>
      </div>
    </div>
  );
}

// ── Licensed States Picker ────────────────────────────────────────────────────

function LicensedStatesPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (s: string) => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto p-2 rounded-md border border-border bg-muted">
        {US_STATES.map((s) => (
          <button key={s} type="button" onClick={() => toggle(s)}
            className={`text-xs font-medium px-2 py-0.5 rounded transition-colors ${value.includes(s) ? "bg-blue-600 text-white" : "bg-card text-muted-foreground border border-border hover:border-blue-300 dark:hover:border-blue-700"}`}>
            {s}
          </button>
        ))}
      </div>
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1.5">
          {value.length} state{value.length !== 1 ? "s" : ""} selected: <span className="font-medium text-foreground">{value.join(", ")}</span>
        </p>
      )}
    </div>
  );
}

// ── Location Dialog ───────────────────────────────────────────────────────────

const emptyLocation = { name: "", address: "", city: "", state: "", zip: "", phone: "", email: "", isPrimary: false };

function LocationDialog({ open, onClose, gaId, existing }: {
  open: boolean; onClose: () => void; gaId: string; existing?: any;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState(emptyLocation);
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (existing) {
      setForm({ name: existing.name || "", address: existing.address || "", city: existing.city || "",
        state: existing.state || "", zip: existing.zip || "", phone: existing.phone || "",
        email: existing.email || "", isPrimary: !!existing.isPrimary });
    } else {
      setForm(emptyLocation);
    }
  }, [existing, open]);

  const mutation = useMutation({
    mutationFn: async (data: typeof form) => {
      if (existing) {
        return apiRequest("PATCH", `/api/ga/${gaId}/locations/${existing.id}`, data);
      }
      return apiRequest("POST", `/api/ga/${gaId}/locations`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "locations"] });
      toast({ title: existing ? "Location updated" : "Location added" });
      onClose();
    },
    onError: () => toast({ title: "Error", description: "Failed to save location.", variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit Location" : "Add Location"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Office Name <span className="text-red-500 dark:text-red-400">*</span></Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Headquarters, LA Branch" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Street Address</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St, Suite 400" />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Los Angeles" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>State</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={form.state} onChange={(e) => set("state", e.target.value)}>
                  <option value="">Select…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>ZIP</Label>
                <Input value={form.zip} onChange={(e) => set("zip", e.target.value)} placeholder="90210" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 000-0000" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="office@agency.com" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Switch checked={form.isPrimary} onCheckedChange={(v) => set("isPrimary", v)} />
            <Label className="cursor-pointer">Set as primary / headquarters location</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate(form)} disabled={!form.name || mutation.isPending}>
            {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save Location"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── State License Dialog ──────────────────────────────────────────────────────

const emptyLicense = { state: "", licenseNumber: "", licenseType: "", status: "active", effectiveDate: "", expirationDate: "", notes: "" };

function LicenseDialog({ open, onClose, gaId, existing }: {
  open: boolean; onClose: () => void; gaId: string; existing?: any;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState(emptyLicense);
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (existing) {
      setForm({ state: existing.state || "", licenseNumber: existing.licenseNumber || "",
        licenseType: existing.licenseType || "", status: existing.status || "active",
        effectiveDate: existing.effectiveDate || "", expirationDate: existing.expirationDate || "",
        notes: existing.notes || "" });
    } else {
      setForm(emptyLicense);
    }
  }, [existing, open]);

  const mutation = useMutation({
    mutationFn: async (data: typeof form) => {
      if (existing) {
        return apiRequest("PATCH", `/api/ga/${gaId}/state-licenses/${existing.id}`, data);
      }
      return apiRequest("POST", `/api/ga/${gaId}/state-licenses`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "state-licenses"] });
      toast({ title: existing ? "License updated" : "License added" });
      onClose();
    },
    onError: () => toast({ title: "Error", description: "Failed to save license.", variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit State License" : "Add State License"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>State <span className="text-red-500 dark:text-red-400">*</span></Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                value={form.state} onChange={(e) => set("state", e.target.value)}>
                <option value="">Select state…</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>License Number</Label>
              <Input value={form.licenseNumber} onChange={(e) => set("licenseNumber", e.target.value)} placeholder="e.g. 0G12345" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>License Type</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                value={form.licenseType} onChange={(e) => set("licenseType", e.target.value)}>
                <option value="">Select type…</option>
                {LICENSE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Effective Date</Label>
              <Input type="date" value={form.effectiveDate} onChange={(e) => set("effectiveDate", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Expiration Date</Label>
              <Input type="date" value={form.expirationDate} onChange={(e) => set("expirationDate", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Status</Label>
              <div className="flex gap-2">
                {LICENSE_STATUSES.map((s) => (
                  <button key={s.value} type="button" onClick={() => set("status", s.value)}
                    className={`flex-1 py-1.5 rounded-md border text-xs font-medium transition-colors ${form.status === s.value ? s.color + " ring-2 ring-offset-1 ring-current" : "bg-card text-muted-foreground border-border hover:border-muted-foreground/30"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Any additional notes about this license…" rows={2} className="resize-none" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate(form)} disabled={!form.state || mutation.isPending}>
            {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save License"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Locations Tab ─────────────────────────────────────────────────────────────

function LocationsTab({ gaId }: { gaId: string }) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const { data: locations = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "locations"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/locations`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/ga/${gaId}/locations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "locations"] });
      toast({ title: "Location removed" });
      setDeleteTarget(null);
    },
    onError: () => toast({ title: "Error", description: "Failed to remove location.", variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Manage your agency's office locations and branches.</p>
        </div>
        <Button onClick={() => { setEditTarget(null); setDialogOpen(true); }} size="sm">
          <Plus className="w-4 h-4 mr-1.5" /> Add Location
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : locations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-border rounded-xl bg-muted">
          <MapPin className="w-10 h-10 text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground">No locations added yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add your headquarters or branch offices</p>
          <Button className="mt-4" size="sm" onClick={() => { setEditTarget(null); setDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-1.5" /> Add First Location
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {locations.map((loc: any) => (
            <Card key={loc.id} className={`transition-shadow hover:shadow-sm ${loc.isPrimary ? "ring-2 ring-blue-200 dark:ring-blue-800" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${loc.isPrimary ? "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400" : "bg-muted text-muted-foreground"}`}>
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-foreground">{loc.name}</p>
                        {loc.isPrimary && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                            <Star className="w-2.5 h-2.5" /> Primary
                          </span>
                        )}
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {(loc.address || loc.city || loc.state) && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3 shrink-0" />
                            {[loc.address, loc.city, loc.state && loc.zip ? `${loc.state} ${loc.zip}` : (loc.state || loc.zip)].filter(Boolean).join(", ")}
                          </p>
                        )}
                        {loc.phone && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3 shrink-0" /> {loc.phone}
                          </p>
                        )}
                        {loc.email && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Globe className="w-3 h-3 shrink-0" /> {loc.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => { setEditTarget(loc); setDialogOpen(true); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                      onClick={() => setDeleteTarget(loc)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <LocationDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditTarget(null); }}
        gaId={gaId} existing={editTarget} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{deleteTarget?.name}</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMutation.mutate(deleteTarget?.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── State Licenses Tab ────────────────────────────────────────────────────────

function StateLicensesTab({ gaId }: { gaId: string }) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const { data: licenses = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "state-licenses"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/state-licenses`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/ga/${gaId}/state-licenses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "state-licenses"] });
      toast({ title: "License removed" });
      setDeleteTarget(null);
    },
    onError: () => toast({ title: "Error", description: "Failed to remove license.", variant: "destructive" }),
  });

  const filtered = filterStatus === "all" ? licenses : licenses.filter((l: any) => l.status === filterStatus);
  const expiringSoon = licenses.filter((l: any) => isExpiringSoon(l.expirationDate)).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {expiringSoon > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800">
              <AlertCircle className="w-3 h-3" /> {expiringSoon} expiring within 60 days
            </span>
          )}
          <div className="flex gap-1">
            {["all", "active", "pending", "expired", "suspended"].map((s) => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors capitalize ${filterStatus === s ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
                {s === "all" ? `All (${licenses.length})` : `${s} (${licenses.filter((l: any) => l.status === s).length})`}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={() => { setEditTarget(null); setDialogOpen(true); }} size="sm">
          <Plus className="w-4 h-4 mr-1.5" /> Add License
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-border rounded-xl bg-muted">
          <Award className="w-10 h-10 text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground">
            {filterStatus === "all" ? "No state licenses added yet" : `No ${filterStatus} licenses`}
          </p>
          {filterStatus === "all" && (
            <>
              <p className="text-xs text-muted-foreground mt-1">Track your state-by-state producer and GA licenses</p>
              <Button className="mt-4" size="sm" onClick={() => { setEditTarget(null); setDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-1.5" /> Add First License
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted border-b border-border">
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">State</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">License #</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">Type</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">Effective</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">Expires</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((lic: any) => {
                const expiring = isExpiringSoon(lic.expirationDate);
                return (
                  <tr key={lic.id} className={`hover:bg-muted/50 ${expiring ? "bg-yellow-50/30 dark:bg-yellow-950/30" : ""}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-bold border border-blue-100 dark:border-blue-800">
                        {lic.state}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground">{lic.licenseNumber || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{lic.licenseType || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{lic.effectiveDate || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className={expiring ? "text-yellow-700 dark:text-yellow-400 font-medium" : "text-muted-foreground"}>
                        {lic.expirationDate || <span className="text-muted-foreground">—</span>}
                        {expiring && <AlertCircle className="w-3 h-3 inline ml-1 text-yellow-500 dark:text-yellow-400" />}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <LicenseStatusBadge status={lic.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => { setEditTarget(lic); setDialogOpen(true); }}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                          onClick={() => setDeleteTarget(lic)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LicenseDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditTarget(null); }}
        gaId={gaId} existing={editTarget} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove License</AlertDialogTitle>
            <AlertDialogDescription>
              Remove the <strong>{deleteTarget?.state}</strong> license record? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMutation.mutate(deleteTarget?.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Main Settings Page ────────────────────────────────────────────────────────

export default function GASettings() {
  const { gaId, gaProfile } = useGaContext();
  const { toast } = useToast();

  const [form, setForm] = useState({
    name: "", description: "", npn: "", fein: "", domicileState: "",
    licensedStates: [] as string[], address: "", city: "", zip: "",
    contactName: "", contactEmail: "", contactPhone: "",
    phoneMain: "", phoneFax: "", website: "", linkedinUrl: "",
  });

  useEffect(() => {
    if (gaProfile) {
      setForm({
        name: gaProfile.name || "",
        description: (gaProfile as any).description || "",
        npn: gaProfile.npn || "",
        fein: gaProfile.fein || "",
        domicileState: gaProfile.domicileState || "",
        licensedStates: (gaProfile as any).licensedStates || [],
        address: (gaProfile as any).address || "",
        city: (gaProfile as any).city || "",
        zip: (gaProfile as any).zip || "",
        contactName: gaProfile.contactName || "",
        contactEmail: gaProfile.contactEmail || "",
        contactPhone: gaProfile.contactPhone || "",
        phoneMain: (gaProfile as any).phoneMain || "",
        phoneFax: (gaProfile as any).phoneFax || "",
        website: (gaProfile as any).website || "",
        linkedinUrl: (gaProfile as any).linkedinUrl || "",
      });
    }
  }, [gaProfile]);

  const { data: notifPrefs } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "notification-prefs"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/notification-prefs`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const [prefMap, setPrefMap] = useState<Record<string, { emailEnabled: boolean; inAppEnabled: boolean }>>({});
  useEffect(() => {
    if (!notifPrefs) return;
    const map: Record<string, { emailEnabled: boolean; inAppEnabled: boolean }> = {};
    ALERT_TYPES.forEach((a) => {
      const found = notifPrefs.find((p: any) => p.alertType === a.key);
      map[a.key] = { emailEnabled: found ? found.emailEnabled : true, inAppEnabled: found ? found.inAppEnabled : true };
    });
    setPrefMap(map);
  }, [notifPrefs]);

  const updateMutation = useMutation({
    mutationFn: async (data: any) => apiRequest("PATCH", `/api/ga/${gaId}/profile`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "profile"] });
      toast({ title: "Profile saved" });
    },
    onError: () => toast({ title: "Error", description: "Failed to save profile.", variant: "destructive" }),
  });

  const notifMutation = useMutation({
    mutationFn: async (prefs: any[]) => apiRequest("PUT", `/api/ga/${gaId}/notification-prefs`, { prefs }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "notification-prefs"] });
      toast({ title: "Notification preferences saved" });
    },
    onError: () => toast({ title: "Error", description: "Failed to save preferences.", variant: "destructive" }),
  });

  const set = (field: string, value: any) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSaveNotifPrefs = () => {
    const prefs = ALERT_TYPES.map((a) => ({
      alertType: a.key,
      emailEnabled: prefMap[a.key]?.emailEnabled ?? true,
      inAppEnabled: prefMap[a.key]?.inAppEnabled ?? true,
    }));
    notifMutation.mutate(prefs);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground mt-0.5">GA profile, locations, licenses, and notification preferences</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="bg-muted p-1">
          <TabsTrigger value="profile">GA Profile</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="licenses">State Licenses</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* ── Profile Tab ──────────────────────────────────────────────── */}
        <TabsContent value="profile" className="mt-4 space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Agency Identity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <LogoUpload
                gaId={gaId || ""}
                logoObjectPath={(gaProfile as any)?.logoObjectPath}
                onUploaded={() => queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "profile"] })}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Agency / GA Name <span className="text-red-500 dark:text-red-400">*</span></Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Premier Benefits GA" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Agency Description</Label>
                  <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                    placeholder="Brief description of your agency, specialties, and value proposition…"
                    rows={3} className="resize-none" />
                  <p className="text-xs text-muted-foreground">Shown on broker-facing portal pages.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <Award className="w-4 h-4" /> Licensing & Compliance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>NPN</Label>
                  <Input value={form.npn} onChange={(e) => set("npn", e.target.value)} placeholder="e.g. 12345678" />
                </div>
                <div className="space-y-1.5">
                  <Label>FEIN</Label>
                  <Input value={form.fein} onChange={(e) => set("fein", e.target.value)} placeholder="XX-XXXXXXX" />
                </div>
                <div className="space-y-1.5">
                  <Label>Domicile State</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={form.domicileState} onChange={(e) => set("domicileState", e.target.value)}>
                    <option value="">Select state…</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Licensed States (quick overview)</Label>
                <LicensedStatesPicker value={form.licensedStates} onChange={(v) => set("licensedStates", v)} />
                <p className="text-xs text-muted-foreground">For detailed license numbers and expiration dates, use the <strong>State Licenses</strong> tab.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Primary Business Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="space-y-1.5 md:col-span-6">
                  <Label>Street Address</Label>
                  <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St, Suite 400" />
                </div>
                <div className="space-y-1.5 md:col-span-3">
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Los Angeles" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>State</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={form.domicileState} onChange={(e) => set("domicileState", e.target.value)}>
                    <option value="">Select…</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5 md:col-span-1">
                  <Label>ZIP</Label>
                  <Input value={form.zip} onChange={(e) => set("zip", e.target.value)} placeholder="90210" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <Phone className="w-4 h-4" /> Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Primary Contact Name</Label>
                  <Input value={form.contactName} onChange={(e) => set("contactName", e.target.value)} placeholder="Full name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Contact Email</Label>
                  <Input type="email" value={form.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} placeholder="contact@agency.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Direct Phone</Label>
                  <Input value={form.contactPhone} onChange={(e) => set("contactPhone", e.target.value)} placeholder="(555) 000-0000" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1 border-t border-border">
                <div className="space-y-1.5">
                  <Label>Main Office Phone</Label>
                  <Input value={form.phoneMain} onChange={(e) => set("phoneMain", e.target.value)} placeholder="(555) 000-0000" />
                </div>
                <div className="space-y-1.5">
                  <Label>Fax</Label>
                  <Input value={form.phoneFax} onChange={(e) => set("phoneFax", e.target.value)} placeholder="(555) 000-0001" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <Globe className="w-4 h-4" /> Online Presence
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-muted-foreground" /> Website</Label>
                  <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://www.youragency.com" />
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" /> LinkedIn</Label>
                  <Input value={form.linkedinUrl} onChange={(e) => set("linkedinUrl", e.target.value)} placeholder="https://linkedin.com/company/your-agency" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => updateMutation.mutate(form)} disabled={updateMutation.isPending} size="lg">
              {updateMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save Profile</>}
            </Button>
          </div>
        </TabsContent>

        {/* ── Locations Tab ────────────────────────────────────────────── */}
        <TabsContent value="locations" className="mt-4">
          <LocationsTab gaId={gaId || ""} />
        </TabsContent>

        {/* ── State Licenses Tab ───────────────────────────────────────── */}
        <TabsContent value="licenses" className="mt-4">
          <StateLicensesTab gaId={gaId || ""} />
        </TabsContent>

        {/* ── Notifications Tab ────────────────────────────────────────── */}
        <TabsContent value="notifications" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <Bell className="w-4 h-4" /> Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-6 pb-2 border-b border-border">
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Alert</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium text-center w-16">Email</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium text-center w-16">In-App</span>
              </div>
              {ALERT_TYPES.map((alert) => (
                <div key={alert.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-6 py-3 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm font-medium text-foreground">{alert.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{alert.description}</p>
                  </div>
                  <div className="flex items-center justify-center w-16">
                    <Switch checked={prefMap[alert.key]?.emailEnabled ?? true}
                      onCheckedChange={(val) => setPrefMap((prev) => ({ ...prev, [alert.key]: { ...prev[alert.key], emailEnabled: val } }))} />
                  </div>
                  <div className="flex items-center justify-center w-16">
                    <Switch checked={prefMap[alert.key]?.inAppEnabled ?? true}
                      onCheckedChange={(val) => setPrefMap((prev) => ({ ...prev, [alert.key]: { ...prev[alert.key], inAppEnabled: val } }))} />
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveNotifPrefs} disabled={notifMutation.isPending}>
                  {notifMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save Preferences</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
